import { Grid } from "@mui/material";
import arca from "assets/images/arca.png";
import logoUpro from "assets/images/logo.png";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import PropTypes from "prop-types";
import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import { valorAPesos } from "utils/formatters";
import { formatPorcentaje, porcentajeAlicuota } from "./voucherIva";

const CONDICIONES_IVA = {
  1: "IVA Responsable Inscripto",
  2: "IVA Responsable no Inscripto",
  3: "IVA no Responsable",
  4: "IVA Sujeto Exento",
  5: "Consumidor Final",
  6: "Responsable Monotributo",
  7: "Sujeto no Categorizado",
  8: "Proveedor del Exterior",
  9: "Cliente del Exterior",
  10: "IVA Liberado - Ley N° 19.640",
  11: "IVA Responsable Inscripto - Agente de Percepción",
  12: "Pequeño Contribuyente Eventual",
  13: "Monotributista Social",
  14: "Pequeño Contribuyente Eventual Social",
};

const TIPOS_DOCUMENTO = { 80: "CUIT", 86: "CUIL", 96: "DNI", 99: "Sin identificar" };
// Monotributo y monotributista social: RG 5003 exige la leyenda en la Factura A.
const CONDICIONES_MONOTRIBUTO = [6, 13];
const LEYENDA_RG_5003 =
  "El crédito fiscal discriminado en el presente comprobante, sólo podrá ser computado a efectos del Régimen de Sostenimiento e Inclusión Fiscal para Pequeños Contribuyentes de la Ley Nº 27.618";
// Los vouchers se consumen primero y se facturan despues al cliente del grupo.
const CONDICION_VENTA = "Cuenta corriente";

const getClientName = (cliente) =>
  cliente?.razonSocial ||
  [cliente?.apellido, cliente?.nombre].filter(Boolean).join(", ") ||
  "Consumidor Final";

const getClientAddress = (cliente) => {
  const street = [cliente?.calle, cliente?.numero].filter(Boolean).join(" ");
  return [street, cliente?.codigoPostal].filter(Boolean).join(" - ") || "Sin especificar";
};

const parseClientSnapshot = (snapshot) => {
  if (!snapshot) return {};
  if (typeof snapshot === "object") return snapshot;
  try {
    const parsed = JSON.parse(snapshot);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const getClientData = (factura) => {
  const snapshot = parseClientSnapshot(factura?.clienteSnapshot);
  const currentClient = factura?.clienteFacturacion || {};
  const keys = [...new Set([...Object.keys(currentClient), ...Object.keys(snapshot)])];
  return Object.fromEntries(keys.map((key) => [key, snapshot[key] ?? currentClient[key]]));
};

const formatDate = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY") : "Sin fecha";

const VoucherFacturaImpresion = ({ factura, descripcion }) => {
  const invoiceRef = useRef(null);
  const [qrCodeURL, setQrCodeURL] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const cliente = getClientData(factura);
  const sede = factura?.sede || {};
  const isFacturaA = factura?.tipoFactura === "A";
  const isFacturaB = factura?.tipoFactura === "B";
  const isExento = factura?.tratamientoIva === "EXENTO";
  const isNoAlcanzado = factura?.tratamientoIva === "NO_ALCANZADO";
  const isNoCorresponde = factura?.tratamientoIva === "NO_CORRESPONDE";
  // En la A el detalle va sin IVA; exento y no alcanzado no tienen IVA que separar.
  const importeDetalle =
    isFacturaA && !isExento && !isNoAlcanzado && !isNoCorresponde ? factura.subtotal21 : factura.total;
  // Documento tal como se informo en el comprobante; el snapshot cubre facturas sin esas columnas.
  const tipoDocumento = factura?.tipo_doc ?? cliente.tipoDocumento;
  const numeroDocumento = factura?.nro_doc || cliente.numeroDocumento || cliente.cuit;
  const requiereLeyendaRg5003 =
    isFacturaA && CONDICIONES_MONOTRIBUTO.includes(Number(cliente.condicionIva));

  useEffect(() => {
    let active = true;
    if (!factura?.url) {
      setQrCodeURL(null);
      return undefined;
    }
    QRCode.toDataURL(factura.url)
      .then((url) => {
        if (active) setQrCodeURL(url);
      })
      .catch(() => {
        if (active) setQrCodeURL(null);
      });
    return () => {
      active = false;
    };
  }, [factura?.url]);

  const handlePrint = async () => {
    const element = invoiceRef.current;
    if (!element || downloading) return;
    const originalWidth = element.style.width;
    const originalMinHeight = element.style.minHeight;
    const originalOverflow = element.style.overflow;
    setDownloading(true);
    try {
      element.style.width = "794px";
      element.style.minHeight = "1123px";
      element.style.overflow = "visible";
      const canvas = await html2canvas(element, { scale: 2, scrollY: -window.scrollY });
      const report = new jsPDF({ orientation: "portrait", compress: true });
      const pdfWidth = report.internal.pageSize.getWidth();
      const pdfHeight = report.internal.pageSize.getHeight();
      report.addImage(canvas.toDataURL("image/jpeg", 1), "JPEG", 0, 0, pdfWidth, pdfHeight);
      report.save(`Factura ${factura.tipoFactura} - ${factura.numeroDeFacturaAfip}.pdf`);
    } finally {
      element.style.width = originalWidth;
      element.style.minHeight = originalMinHeight;
      element.style.overflow = originalOverflow;
      setDownloading(false);
    }
  };

  return (
    <SoftBox>
      <SoftBox sx={{ display: { xs: "none", sm: "block" } }}>
        <Grid container direction="column" alignItems="center" p={2}>
          <Grid
            ref={invoiceRef}
            sx={{
              width: "794px",
              minHeight: "1123px",
              backgroundColor: "#fff",
              p: 4,
              boxShadow: "0 0 5px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              fontFamily: "Arial, sans-serif",
            }}
          >
            <Grid
              container
              justifyContent="space-between"
              borderBottom="2px solid #000"
              pb={2}
              alignItems="center"
              spacing={2}
            >
              <Grid item xs={6} display="flex" gap={2} alignItems="center">
                <SoftBox>
                  <img src={logoUpro} alt="Logo UPrO" style={{ width: "120px" }} />
                </SoftBox>
                <SoftBox display="flex" flexDirection="column">
                  <SoftTypography variant="h6">
                    {sede.nombreFantasia || sede.nombre || "UPrO"}
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Dirección: <strong>{sede.direccion || "Sin especificar"}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    CUIT: <strong>{sede.cuit || "Sin especificar"}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Condición frente al IVA:{" "}
                    <strong>{CONDICIONES_IVA[sede.condicionIva] || "Sin especificar"}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Inicio de actividades: <strong>{formatDate(sede.incioActividades)}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Email: <strong>{sede.email || "Sin especificar"}</strong>
                  </SoftTypography>
                </SoftBox>
              </Grid>
              <Grid item xs={6} display="flex" gap={2} alignItems="center">
                <SoftBox
                  textAlign="center"
                  display="flex"
                  flexDirection="column"
                  px={2}
                  py={1}
                  sx={{ border: "1px solid #000", width: "fit-content" }}
                >
                  <SoftTypography variant="h2" fontWeight="bold">
                    {factura.tipoFactura}
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    COD: {String(factura.tipo_cbte || "").padStart(3, "0")}
                  </SoftTypography>
                </SoftBox>
                <SoftBox display="flex" flexDirection="column">
                  <SoftTypography variant="h6">FACTURA</SoftTypography>
                  <SoftTypography variant="caption">
                    N° de comprobante:{" "}
                    <strong>{factura.numeroDeFacturaAfip || "Sin número"}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Punto de Venta:{" "}
                    <strong>{factura.pto_vta || sede.ptoVenta || "Sin número"}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Fecha de Emisión: <strong>{formatDate(factura.fechaFactura)}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    Ingresos Brutos: <strong>{sede.ingresosBrutos || "Sin especificar"}</strong>
                  </SoftTypography>
                </SoftBox>
              </Grid>
            </Grid>

            <SoftBox
              display="flex"
              flexDirection="column"
              my={2}
              py={1.5}
              px={1.75}
              border="1px solid #888"
            >
              <SoftTypography variant="h6">Datos del cliente</SoftTypography>
              <SoftTypography variant="caption">
                Apellido y Nombre / Razón Social: <strong>{getClientName(cliente)}</strong>
              </SoftTypography>
              <SoftTypography variant="caption">
                {TIPOS_DOCUMENTO[tipoDocumento] || "Documento"}:{" "}
                <strong>{numeroDocumento || "Sin especificar"}</strong>
              </SoftTypography>
              <SoftTypography variant="caption">
                Condición frente al IVA:{" "}
                <strong>
                  {CONDICIONES_IVA[cliente.condicionIva] ||
                    (factura.tipoFactura === "C" ? "Consumidor Final" : "Sin especificar")}
                </strong>
              </SoftTypography>
              <SoftTypography variant="caption">
                Domicilio: <strong>{getClientAddress(cliente)}</strong>
              </SoftTypography>
            </SoftBox>

            <Grid container mt={1} border="1px solid #ccc">
              <Grid container sx={{ backgroundColor: "#f5f5f5", p: 1 }}>
                <Grid item xs={2}>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Cantidad
                  </SoftTypography>
                </Grid>
                <Grid item xs={5}>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Descripción
                  </SoftTypography>
                </Grid>
                <Grid item xs={2.5} textAlign="right">
                  <SoftTypography variant="h6" fontWeight="bold">
                    Precio unit.{isFacturaA ? " s/IVA" : ""}
                  </SoftTypography>
                </Grid>
                <Grid item xs={2.5} textAlign="right">
                  <SoftTypography variant="h6" fontWeight="bold">
                    Subtotal{isFacturaA ? " s/IVA" : ""}
                  </SoftTypography>
                </Grid>
              </Grid>
              <Grid container sx={{ p: 1, borderTop: "1px dashed #ccc" }}>
                <Grid item xs={2}>
                  <SoftTypography variant="h6" fontWeight="regular">
                    1
                  </SoftTypography>
                </Grid>
                <Grid item xs={5}>
                  <SoftTypography variant="h6" fontWeight="regular">
                    {descripcion}
                  </SoftTypography>
                </Grid>
                <Grid item xs={2.5} textAlign="right">
                  <SoftTypography variant="h6" fontWeight="regular">
                    {valorAPesos(importeDetalle)}
                  </SoftTypography>
                </Grid>
                <Grid item xs={2.5} textAlign="right">
                  <SoftTypography variant="h6" fontWeight="regular">
                    {valorAPesos(importeDetalle)}
                  </SoftTypography>
                </Grid>
              </Grid>
            </Grid>

            <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mt={3}>
              <SoftBox display="flex" gap={2} alignItems="center">
                {qrCodeURL ? (
                  <SoftBox display="flex" flexDirection="column" alignItems="center">
                    <img
                      src={qrCodeURL}
                      alt="Código QR de la factura"
                      style={{ width: "120px", height: "120px" }}
                    />
                    <img
                      src={arca}
                      alt="ARCA"
                      style={{ width: "100px", height: "40px", marginTop: "8px" }}
                    />
                  </SoftBox>
                ) : null}
                <SoftBox display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                  <SoftTypography variant="h7">
                    <strong>CAE:</strong> {factura.CAE || "Sin CAE"}
                  </SoftTypography>
                  <SoftTypography variant="h7">
                    <strong>Fecha de vto. CAE:</strong> {formatDate(factura.CAEFchVto)}
                  </SoftTypography>
                  {isFacturaB ? (
                    <SoftBox
                      p={1}
                      border="1px solid #000"
                      display="flex"
                      flexDirection="column"
                      gap={1}
                    >
                      <SoftTypography variant="caption">
                        Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)
                      </SoftTypography>
                      <SoftTypography variant="caption">
                        IVA Contenido: <strong>{valorAPesos(factura.imp_iva21 || 0)}</strong>
                      </SoftTypography>
                      <SoftTypography variant="caption">
                        Otros Impuestos Nacionales Indirectos: <strong>{valorAPesos(0)}</strong>
                      </SoftTypography>
                    </SoftBox>
                  ) : null}
                  {requiereLeyendaRg5003 ? (
                    <SoftTypography variant="caption" sx={{ maxWidth: "320px" }}>
                      {LEYENDA_RG_5003}
                    </SoftTypography>
                  ) : null}
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                {isNoCorresponde ? (
                  <>
                    <SoftTypography variant="h7">
                      <strong>Importe neto:</strong> {valorAPesos(factura.total)}
                    </SoftTypography>
                  </>
                ) : isExento ? (
                  <>
                    <SoftTypography variant="h7">
                      <strong>Importe exento:</strong> {valorAPesos(factura.total)}
                    </SoftTypography>
                    <SoftTypography variant="caption">Operación exenta de IVA</SoftTypography>
                  </>
                ) : isNoAlcanzado ? (
                  <>
                    <SoftTypography variant="h7">
                      <strong>Importe no gravado:</strong> {valorAPesos(factura.total)}
                    </SoftTypography>
                    <SoftTypography variant="caption">
                      Operación no alcanzada por el IVA
                    </SoftTypography>
                  </>
                ) : isFacturaA ? (
                  <>
                    <SoftTypography variant="h7">
                      <strong>Importe neto gravado:</strong> {valorAPesos(factura.subtotal21 || 0)}
                    </SoftTypography>
                    <SoftTypography variant="h7">
                      <strong>
                        IVA {formatPorcentaje(porcentajeAlicuota(factura.alicuotaIvaId))}:
                      </strong>{" "}
                      {valorAPesos(factura.imp_iva21 || 0)}
                    </SoftTypography>
                  </>
                ) : null}
                <SoftTypography variant="h6">
                  <strong>Importe total:</strong> {valorAPesos(factura.total)}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </Grid>

          <SoftBox mt={2}>
            <SoftButton onClick={handlePrint} disabled={downloading} circular color="uproGreen">
              {downloading ? "Generando PDF..." : "Descargar PDF"}
            </SoftButton>
          </SoftBox>
        </Grid>
      </SoftBox>
      <SoftBox sx={{ display: { xs: "block", sm: "none" } }}>
        <SinDatos
          titulo="Funcionalidad no disponible"
          mensaje="La visualización de la factura solo está disponible en la versión de escritorio."
          icono="receipt_long"
        />
      </SoftBox>
    </SoftBox>
  );
};

VoucherFacturaImpresion.propTypes = {
  factura: PropTypes.object.isRequired,
  descripcion: PropTypes.string.isRequired,
};

export default VoucherFacturaImpresion;
